import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { MessageSquarePlus, X, Loader2, CheckCircle } from 'lucide-react';
import { useActiveModal, detectarModalAtivoNoDom } from '../contexts/ActiveModalContext';

/** Rótulos amigáveis para rotas conhecidas (contexto do reporte). */
const PAGINA_TITULOS = {
  '/app/dashboard': 'Dashboard',
  '/app/companies': 'Empresas',
  '/app/leads': 'Leads',
  '/app/solicitacoes-sistema': 'Solicitações do sistema',
  '/app/receivings': 'Recebimentos',
  '/app/estoque': 'Estoque',
  '/app/separacao-pesagem': 'Separação e Pesagem',
  '/app/planejamento-producao': 'Planejamento',
  '/app/producao': 'Produção',
  '/app/fluxo-setorial': 'Fluxo setorial',
  '/app/produtos-finais': 'Produtos finais',
  '/app/users': 'Usuários',
  '/app/settings': 'Configurações',
  '/app/reports': 'Relatórios',
  '/app/documents': 'Documentos',
  '/app/marketplace': 'Marketplace',
  '/app/cotacoes': 'Cotações',
  '/app/rastreabilidade': 'Rastreabilidade',
  '/app/trabalho': 'Estação de trabalho',
};

function tituloDaPagina(pathname) {
  if (!pathname) return 'Página atual';
  if (PAGINA_TITULOS[pathname]) return PAGINA_TITULOS[pathname];
  if (pathname.startsWith('/app/fornecedores')) return 'Fornecedores';
  if (pathname.startsWith('/app/insumos')) return 'Insumos';
  if (pathname.startsWith('/app/kiosk')) return 'Kiosk de produção';
  if (pathname.startsWith('/app/equipamentos')) return 'Equipamentos';
  if (pathname.startsWith('/app/dashboard')) return 'Dashboard';
  return document.title || pathname;
}

function resolverModalAtivo(activeFromContext) {
  // Preferência: registro explícito via contexto (mais confiável)
  if (activeFromContext?.title) {
    return {
      id: activeFromContext.id || '',
      title: activeFromContext.title,
      extra: activeFromContext.extra || null
    };
  }
  // Fallback: detectar overlay/dialog visível no DOM (antes de abrir o reporte)
  return detectarModalAtivoNoDom();
}

/**
 * Botão flutuante global para reportar erro ou ajuste necessário.
 * Captura página atual e, se houver, o modal ativo.
 */
const ReportIssueButton = () => {
  const location = useLocation();
  const { activeModal } = useActiveModal();
  const [open, setOpen] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);
  /** Snapshot do modal no momento do clique em Reportar (antes de abrir este dialog). */
  const [modalSnapshot, setModalSnapshot] = useState(null);

  const paginaPath = location.pathname || '/app';
  const paginaTitulo = tituloDaPagina(paginaPath);
  const paginaUrl =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search || ''}`
      : paginaPath;

  const abrir = () => {
    // Capturar ANTES de montar o dialog de reporte
    const modal = resolverModalAtivo(activeModal);
    setModalSnapshot(modal);
    setOpen(true);
    setErro('');
    setSucesso(false);
  };

  const fechar = () => {
    if (enviando) return;
    setOpen(false);
    setErro('');
    setSucesso(false);
    setMensagem('');
    setModalSnapshot(null);
  };

  const enviar = async () => {
    const texto = mensagem.trim();
    if (texto.length < 5) {
      setErro('Descreva o problema com pelo menos 5 caracteres.');
      return;
    }
    setEnviando(true);
    setErro('');
    try {
      const createSolicitacaoSistema = httpsCallable(functions, 'createSolicitacaoSistema');
      const result = await createSolicitacaoSistema({
        mensagem: texto,
        pagina_path: paginaPath,
        pagina_titulo: paginaTitulo,
        pagina_url: paginaUrl,
        modal_ativo: !!modalSnapshot?.title,
        modal_id: modalSnapshot?.id || '',
        modal_titulo: modalSnapshot?.title || '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
      });
      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Falha ao registrar');
      }
      setSucesso(true);
      setMensagem('');
      setTimeout(() => {
        setOpen(false);
        setSucesso(false);
        setModalSnapshot(null);
      }, 1800);
    } catch (e) {
      console.error(e);
      setErro(e?.message || e?.details || 'Não foi possível enviar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        className="fixed bottom-5 right-5 z-[80] flex items-center gap-2 rounded-full bg-amber-600 text-white shadow-lg hover:bg-amber-700 px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
        title="Reportar erro ou ajuste"
        aria-label="Reportar erro ou ajuste"
      >
        <MessageSquarePlus className="h-5 w-5 shrink-0" />
        <span className="hidden sm:inline">Reportar</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-black/50"
          data-report-issue-root="true"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-issue-title"
            data-report-issue-root="true"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 id="report-issue-title" className="font-semibold text-gray-900 text-sm sm:text-base">
                Reportar erro ou ajuste
              </h2>
              <button
                type="button"
                onClick={fechar}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                aria-label="Fechar"
                disabled={enviando}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {sucesso ? (
                <div className="flex flex-col items-center gap-2 py-6 text-emerald-700">
                  <CheckCircle className="h-10 w-10" />
                  <p className="font-medium text-sm">Solicitação registrada. Obrigado!</p>
                </div>
              ) : (
                <>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 space-y-1.5">
                    <p>
                      <span className="font-medium text-gray-800">Página:</span> {paginaTitulo}
                    </p>
                    <p className="font-mono text-gray-500 break-all">{paginaPath}</p>
                    {modalSnapshot?.title ? (
                      <p>
                        <span className="font-medium text-gray-800">Modal ativo:</span>{' '}
                        {modalSnapshot.title}
                      </p>
                    ) : (
                      <p className="text-gray-400">Nenhum modal ativo no momento do reporte.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descreva o erro ou o ajuste necessário
                    </label>
                    <textarea
                      value={mensagem}
                      onChange={(e) => setMensagem(e.target.value)}
                      rows={5}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Ex.: No modal de consumo, ao registrar o lote o botão fica carregando…"
                      disabled={enviando}
                      maxLength={4000}
                    />
                    <p className="text-[11px] text-gray-400 mt-1 text-right">{mensagem.length}/4000</p>
                  </div>

                  {erro && (
                    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {erro}
                    </p>
                  )}

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={fechar}
                      disabled={enviando}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={enviar}
                      disabled={enviando || mensagem.trim().length < 5}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    >
                      {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Enviar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportIssueButton;
